import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePermissions } from '@/contexts/PermissionContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { AGENCY_METHODS, buildMarkFiledBody } from '@/components/incidents/injuryRegisterUtils';
import { ScrollArea } from '../../components/ui/scroll-area';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../components/ui/dialog';
import {
  AlertTriangle, Clock, CheckCircle2, Bell,
  FileText, ArrowRight, Loader2, Scale,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  overdue: { color: 'bg-red-500', text: 'text-red-700', bg: 'bg-red-50', border: 'border-red-200', label: 'OVERDUE', icon: AlertTriangle, pulse: true },
  due_soon: { color: 'bg-amber-500', text: 'text-amber-700', bg: 'bg-amber-50', border: 'border-amber-200', label: 'DUE SOON', icon: Clock, pulse: false },
  on_track: { color: 'bg-emerald-500', text: 'text-emerald-700', bg: 'bg-emerald-50', border: 'border-emerald-200', label: 'ON TRACK', icon: Clock, pulse: false },
  submitted: { color: 'bg-slate-400', text: 'text-slate-500', bg: 'bg-slate-50', border: 'border-slate-200', label: 'FILED', icon: CheckCircle2, pulse: false },
  unknown: { color: 'bg-slate-300', text: 'text-slate-500', bg: 'bg-slate-50', border: 'border-slate-200', label: '—', icon: Clock, pulse: false },
};

const fmtRpDeadline = (deadline) => {
  if (!deadline) return null;
  try {
    const now = new Date();
    const dl = new Date(deadline);
    const diff = dl - now;
    const hrs = Math.floor(Math.abs(diff) / 3600000);
    if (diff < 0) return { status: 'overdue', overdue_display: hrs < 24 ? `${hrs}h overdue` : `${Math.floor(hrs/24)}d overdue`, remaining_display: '' };
    if (hrs < 24) return { status: 'due_soon', remaining_display: `${hrs}h left`, overdue_display: '' };
    return { status: 'on_track', remaining_display: `${Math.floor(hrs/24)}d left`, overdue_display: '' };
  } catch { return null; }
};

const DeadlinePill = ({ status }) => {
  const cfg = STATUS_CONFIG[status.status] || STATUS_CONFIG.unknown;
  const Icon = cfg.icon;
  return (
    <div className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${cfg.bg} ${cfg.text} ${cfg.border} border`}>
      {cfg.pulse && <span className="relative flex h-2 w-2"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75" /><span className="relative inline-flex rounded-full h-2 w-2 bg-red-500" /></span>}
      <Icon size={11} />
      {status.status === 'overdue' && <span>{status.overdue_display}</span>}
      {status.status === 'due_soon' && <span>{status.remaining_display}</span>}
      {status.status === 'on_track' && <span>{status.remaining_display}</span>}
      {status.status === 'submitted' && <span>Filed</span>}
    </div>
  );
};

const TrackerRow = ({ item, onMarkFiled, canFile }) => {
  const navigate = useNavigate();
  const isRp = item.item_type === 'rp_incident' || item.item_type === 'rp_monthly';

  // For RP items, compute deadline status from rp fields
  const rpDeadlineStatus = isRp ? fmtRpDeadline(item.rp_commission_deadline) : null;

  // For standard items
  const allStatuses = isRp ? [rpDeadlineStatus].filter(Boolean) : [
    item.immediate_notification, item.five_day_form,
    item.safework_immediate, item.safework_written, item.wc_48h,
  ].filter(Boolean);
  const worstStatus = allStatuses.some(s => s?.status === 'overdue')
    ? 'overdue' : allStatuses.some(s => s?.status === 'due_soon')
    ? 'due_soon' : 'on_track';
  const rowBg = worstStatus === 'overdue' ? 'bg-red-50/50 hover:bg-red-50' : worstStatus === 'due_soon' ? 'bg-amber-50/30 hover:bg-amber-50' : 'hover:bg-slate-50';

  const handleClick = () => {
    navigate(`/incidents/${item.id}`);
  };

  return (
    <div
      className={`px-3 py-2 rounded-md border border-gray-100 transition-colors cursor-pointer ${rowBg}`}
      data-testid={`tracker-row-${item.id}${item.rp_id ? `-${item.rp_id}` : ''}`}
      onClick={handleClick}
    >
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-xs font-bold text-slate-900 shrink-0">{item.incident_number}</span>
        {isRp && <span className="text-[9px] font-semibold text-violet-600 bg-violet-50 px-1 rounded"><Scale size={8} className="inline mr-0.5" />RP</span>}
        {!isRp && item.is_ndis && <span className="text-[9px] font-semibold text-rose-600 bg-rose-50 px-1 rounded">NDIS</span>}
        {!isRp && item.is_whs && <span className="text-[9px] font-semibold text-orange-600 bg-orange-50 px-1 rounded">WHS</span>}
        {!isRp && item.is_wc && <span className="text-[9px] font-semibold text-amber-700 bg-amber-50 px-1 rounded">WC</span>}
        <span className="text-[11px] text-slate-500 truncate min-w-0 flex-1">{item.description?.slice(0, 50)}</span>
        <div className="flex items-center gap-2 shrink-0 flex-wrap">
          {/* RP item deadlines */}
          {isRp && rpDeadlineStatus && item.rp_commission_status !== 'submitted' && (
            <>
              <span className="text-[10px] text-violet-500">{item.rp_deadline_type === '24_hours' ? '24h' : item.item_type === 'rp_monthly' ? 'Monthly' : '5d'}:</span>
              <DeadlinePill status={rpDeadlineStatus} />
            </>
          )}
          {isRp && item.rp_commission_status === 'submitted' && (
            <DeadlinePill status={{ status: 'submitted' }} />
          )}

          {/* Standard incident deadlines */}
          {!isRp && item.is_ndis && item.immediate_notification && (
            <>
              <span className="text-[10px] text-slate-400">{item.ndis_clock_label === '5d' ? '5d:' : '24h:'}</span>
              <DeadlinePill status={item.immediate_notification} />
              {item.immediate_notification.status !== 'submitted' && canFile && (
                <button type="button" className="text-[10px] text-blue-600 hover:text-blue-800 font-medium" onClick={(e) => { e.stopPropagation(); onMarkFiled(item, 'immediate'); }}>File</button>
              )}
              {item.five_day_form && (
                <>
                  <span className="text-[10px] text-slate-300">|</span>
                  <span className="text-[10px] text-slate-400">5d:</span>
                  <DeadlinePill status={item.five_day_form} />
                  {item.five_day_form.status !== 'submitted' && canFile && (
                    <button type="button" className="text-[10px] text-blue-600 hover:text-blue-800 font-medium" onClick={(e) => { e.stopPropagation(); onMarkFiled(item, 'five_day'); }}>File</button>
                  )}
                </>
              )}
            </>
          )}
          {!isRp && item.is_whs && item.safework_immediate && (
            <>
              {item.is_ndis && <span className="text-[10px] text-slate-300">|</span>}
              <span className="text-[10px] text-orange-500">SW:</span>
              <DeadlinePill status={item.safework_immediate} />
              {item.safework_immediate.status !== 'submitted' && canFile && (
                <button type="button" className="text-[10px] text-blue-600 hover:text-blue-800 font-medium" onClick={(e) => { e.stopPropagation(); onMarkFiled(item, 'safework_immediate'); }}>File</button>
              )}
            </>
          )}
          {!isRp && item.is_wc && item.wc_48h && (
            <>
              {(item.is_ndis || item.is_whs) && <span className="text-[10px] text-slate-300">|</span>}
              <span className="text-[10px] text-amber-600">WC 48h:</span>
              <DeadlinePill status={item.wc_48h} />
              {item.wc_48h.status !== 'submitted' && canFile && (
                <button type="button" className="text-[10px] text-blue-600 hover:text-blue-800 font-medium" onClick={(e) => { e.stopPropagation(); onMarkFiled(item, 'wc_48h'); }}>File</button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const NotificationTrackerWidget = () => {
  const { hasPermission, isAdmin } = usePermissions();
  const canFile = isAdmin || hasPermission('incident.section.compliance.edit');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filingItem, setFilingItem] = useState(null); // {item, type}
  const [refNumber, setRefNumber] = useState('');
  const [channel, setChannel] = useState('portal');
  const [filing, setFiling] = useState(false);

  const token = localStorage.getItem('access_token');

  const fetchTracker = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/incidents/notification-tracker`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) setData(await res.json());
    } catch (e) { console.error('Tracker fetch error:', e); }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { fetchTracker(); }, [fetchTracker]);

  // Refresh every 60 seconds for live countdowns
  useEffect(() => {
    const interval = setInterval(fetchTracker, 60000);
    return () => clearInterval(interval);
  }, [fetchTracker]);

  const handleMarkFiled = (item, type) => {
    setFilingItem({ item, type });
    setChannel('portal');
    setRefNumber(
      type === 'wc_48h' ? (item.wc_reference_number || '')
        : type.startsWith('safework') ? (item.safework_reference_number || '')
        : (item.ndis_reference_number || '')
    );
  };

  const confirmFiling = async () => {
    if (!filingItem) return;
    setFiling(true);
    try {
      const res = await fetch(`${API}/api/incidents/${filingItem.item.id}/mark-notification-filed`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(buildMarkFiledBody(filingItem.type, refNumber, channel)),
      });
      if (res.ok) {
        setFilingItem(null);
        setRefNumber('');
        fetchTracker();
      }
    } catch (e) { console.error('Filing error:', e); }
    finally { setFiling(false); }
  };

  if (loading || !data || data.total === 0) return null;

  return (
    <>
      <Card className="border-none shadow-sm bg-white dark:bg-gray-900 hover:shadow-md transition-all h-full flex flex-col" data-testid="notification-tracker-widget">
        <CardHeader className="pb-1 pt-3 px-4 flex flex-row items-center justify-between space-y-0">
          <div className="flex items-center gap-2">
            <CardTitle className="text-sm font-semibold text-foreground">Notification Tracker</CardTitle>
            {data.overdue > 0 && (
              <Badge className="bg-red-500 text-white text-[10px] px-1.5 py-0 animate-pulse">
                {data.overdue} overdue
              </Badge>
            )}
            {data.due_soon > 0 && (
              <Badge className="bg-amber-500 text-white text-[10px] px-1.5 py-0">
                {data.due_soon} due soon
              </Badge>
            )}
            {data.rp_incident_count > 0 && (
              <Badge className="bg-violet-500 text-white text-[10px] px-1.5 py-0">
                {data.rp_incident_count} RP
              </Badge>
            )}
          </div>
          <div className="p-2 rounded-lg bg-rose-50 text-rose-700">
            <Bell className="h-4 w-4" />
          </div>
        </CardHeader>
        <CardContent className="px-4 pb-3 pt-1">
          <ScrollArea className={data.total > 4 ? 'h-[180px]' : ''}>
            <div className="space-y-1">
              {data.items.map((item, idx) => (
                <TrackerRow key={`${item.id}-${item.rp_id || idx}`} item={item} onMarkFiled={handleMarkFiled} canFile={canFile} />
              ))}
            </div>
          </ScrollArea>
          {data.total > 0 && (
            <div className="pt-1.5 mt-1.5 border-t flex items-center justify-between">
              <span className="text-[11px] text-muted-foreground">{data.total} active</span>
              <Button variant="link" size="sm" className="h-auto p-0 text-[11px] text-rose-600" onClick={() => window.location.href = '/incidents'}>
                View All <ArrowRight size={11} className="ml-0.5" />
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Mark as Filed Dialog */}
      <Dialog open={!!filingItem} onOpenChange={(open) => { if (!open) { setFilingItem(null); setRefNumber(''); setChannel('portal'); } }}>
        <DialogContent className="sm:max-w-md" data-testid="mark-filed-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText size={18} />
              Mark Notification as Filed
            </DialogTitle>
          </DialogHeader>
          {filingItem && (
            <div className="space-y-4">
              <div className="bg-slate-50 rounded-lg p-3 space-y-1">
                <p className="text-sm font-semibold">{filingItem.item.incident_number}</p>
                <p className="text-xs text-slate-500">{filingItem.item.description}</p>
                <Badge variant="outline" className="text-xs mt-1">
                  {filingItem.type === 'immediate' ? '24-Hour NDIS Notification' : 
                   filingItem.type === 'five_day' ? '5-Day NDIS Report' : 
                   filingItem.type === 'safework_immediate' ? 'SafeWork Immediate Notification' : 
                   filingItem.type === 'wc_48h' ? 'Workers compensation 48h insurer notification' :
                   'SafeWork 48h Written Report'}
                </Badge>
              </div>
              <div>
                <Label className="text-sm font-medium">Method</Label>
                <Select value={channel} onValueChange={setChannel}>
                  <SelectTrigger className="mt-1" data-testid="mark-filed-method"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {AGENCY_METHODS.map((opt) => <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-sm font-medium">
                  {filingItem.type === 'wc_48h' ? 'Notification / claim number' : filingItem.type.startsWith('safework') ? 'SafeWork Reference Number' : 'NDIS Commission Reference Number'}
                </Label>
                <Input
                  value={refNumber}
                  onChange={(e) => setRefNumber(e.target.value)}
                  placeholder={filingItem.type.startsWith('safework') ? 'e.g., SW-2026-001234' : 'e.g., NDIS-2026-001234'}
                  className="mt-1"
                  data-testid="ref-number-input"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Enter the reference number received after filing.
                </p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setFilingItem(null); setRefNumber(''); }}>Cancel</Button>
            <Button onClick={confirmFiling} disabled={filing} data-testid="confirm-filing-btn">
              {filing ? <><Loader2 size={14} className="animate-spin mr-1" />Filing...</> : 'Confirm Filed'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default NotificationTrackerWidget;
